import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Skeleton, SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash';

// Images are framed in a portrait-ish box (height ≈ width * 0.85).
// Using a CSS aspect-ratio means the slide reserves height before the image
// loads, so the nav buttons stay vertically centred and there's no layout jump.
const IMAGE_ASPECT_RATIO = '20 / 17';

const GalleryWrap = styled('div')(({ theme }) => ({
    width: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    [theme.breakpoints.only('xs')]: {
        borderRadius: 0,
    },
    '& .image-gallery-slide-wrapper': {
        margin: 'auto',
    },
    '& .image-gallery-content': {
        maxWidth: 1280,
        margin: 'auto',
    },
    '& .image-gallery-slide .image-gallery-image': {
        width: '100%',
        height: 'auto',
        aspectRatio: IMAGE_ASPECT_RATIO,
        maxHeight: 'unset',
        objectFit: 'cover',
        objectPosition: '50% 25%',
    },
    // Fullscreen (CSS modal): lift above all site chrome and let the image fit
    // the viewport uncropped instead of being locked to the thumbnail ratio.
    '& .image-gallery.fullscreen-modal': {
        zIndex: theme.zIndex.modal + 1,
    },
    '& .image-gallery.fullscreen-modal .image-gallery-image': {
        width: 'auto',
        height: 'auto',
        aspectRatio: 'auto',
        maxHeight: 'calc(100vh - 80px)',
        objectFit: 'contain',
        borderRadius: 0,
    },
}));

// `sx` is applied last via emotion, so it reliably overrides the IconButton's
// own base `position: relative` (the legacy JSS styles did not, which previously
// dropped the buttons above the image).
const navSx =
    (side: 'left' | 'right'): SxProps<Theme> =>
    (theme) => ({
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [side]: 15,
        zIndex: 7,
        padding: '6px',
        opacity: 0.75,
        color: theme.palette.text.primary,
        background: theme.palette.common.white,
        '&:hover': {
            background: theme.palette.common.white,
            opacity: 0.9,
        },
        [theme.breakpoints.only('xs')]: {
            [side]: 14,
            color: theme.palette.common.white,
            background: 'transparent',
            '&:hover': { background: 'transparent' },
            '& .MuiSvgIcon-root': { fontSize: 40 },
        },
    });

interface ImageItem {
    readonly url: string;
    readonly alt: string;
}

interface InstagramImageGalleryProps {
    readonly images: ImageItem[];
}

export default function InstagramImageGallery({ images }: InstagramImageGalleryProps) {
    if (isEmpty(images)) {
        return (
            <div style={{ width: '100%' }}>
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    sx={{ aspectRatio: IMAGE_ASPECT_RATIO, borderRadius: { xs: 0, sm: '10px' } }}
                />
            </div>
        );
    }

    return (
        <GalleryWrap>
            <ImageGallery
                showPlayButton={false}
                showThumbnails={false}
                useBrowserFullscreen={false}
                lazyLoad
                slideDuration={200}
                swipeThreshold={25}
                items={images.map((image) => ({
                    original: image.url,
                    originalAlt: image.alt,
                }))}
                renderLeftNav={(onClick, disabled) => (
                    <IconButton aria-label="Previous image" disabled={disabled} onClick={onClick} sx={navSx('left')}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}
                renderRightNav={(onClick, disabled) => (
                    <IconButton aria-label="Next image" disabled={disabled} onClick={onClick} sx={navSx('right')}>
                        <ChevronRightIcon />
                    </IconButton>
                )}
            />
        </GalleryWrap>
    );
}
